import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { DeviceType, toBoolean } from "@obg/ngx.global";
import { compact, head, kebabCase, unescape } from "lodash-es";
import { forkJoin, Observable } from "rxjs";
import { map, take, tap } from "rxjs/operators";

import { ContentClient } from "../content/content.client";
import { MetadataDeviceType } from "../model";
import { CoreContentState } from "../state";

import { MenuAction } from "./menu.action";
import { MenuItem, MenuItemGroup, MenuResponseItem } from "./menu.model";

/**
 * @deprecated [fabric-deprecated] - Use fabric MenuService from svc-content instead
 */
@Injectable({
	providedIn: "root",
})
export class MenuService {
	constructor(
		private client: ContentClient,
		private store: Store<CoreContentState>
	) {}

	private static mapDeviceTypeToMetaDeviceType(device: DeviceType) {
		switch (device) {
			case DeviceType.Mobile:
				return MetadataDeviceType.Mobile;
			case DeviceType.Tablet:
				return MetadataDeviceType.Tablet;
			default:
				return MetadataDeviceType.Desktop;
		}
	}

	private static filterByDeviceType(item: MenuResponseItem, deviceType?: DeviceType) {
		const metaDeviceType = item.content.metadata?.deviceType;
		return (
			!deviceType ||
			!metaDeviceType ||
			metaDeviceType.includes(MetadataDeviceType.Any) ||
			metaDeviceType.includes(MenuService.mapDeviceTypeToMetaDeviceType(deviceType))
		);
	}

	// todo: API should flatten values
	private static normalizeItem({ documentKey: key, content }: MenuResponseItem): MenuItem {
		const link = content.link?.[0];
		return {
			key,
			link: link && {
				...link,
				url: unescape(link.url), // todo: OBGB-2005 remove unescaping
			},
			title: unescape(content.title?.[0]),
			route: content.route?.[0],
			iconTag: content.iconTag?.[0],
			icon: content.icon?.[0],
			visualTags: compact(content.visualTag),
			visualTag: compact(content.visualTag),
			userState: content.metadata?.userState ?? [],
			jurisdiction: content.metadata?.jurisdiction ?? [],
			commandName: content.metadata?.commandName?.[0],
			visualContent: content.visualContent?.[0],
			enableColouredIcon: toBoolean(head(content.metadata?.colouredIcon)) ?? false,
			colouredIcon: head(content.colouredIcon),
		};
	}

	private static normalizeGroups(groups: Record<string, MenuItem[]>): Record<string, MenuItemGroup> {
		return Object.entries(groups).reduce(
			(menus, [key, items]) => ({
				...menus,
				[key]: {
					items: items
						.filter((child) => !items.some((parent) => MenuService.isItemParent(parent, child)))
						.map((item) => {
							const children = items.filter((child) => MenuService.isItemParent(item, child));

							return children.length ? { ...item, children } : item;
						}),
					key,
				},
			}),
			{}
		);
	}

	private static isItemParent(parent: MenuItem, child: MenuItem) {
		return parent.key !== child.key && child.key.startsWith(parent.key);
	}

	/**
	 * @deprecated [fabric-deprecated] - Use fabric ContentService.menu$ from svc-content instead
	 */
	getAllByName(menuNames: string[], deviceType?: DeviceType): Observable<Record<string, MenuItemGroup>> {
		return forkJoin(
			menuNames.map((menuName) =>
				this.client.getMenuItems(menuName).pipe(
					tap((fetchedRawData) =>
						this.store.dispatch(MenuAction.menuItemsFetched(fetchedRawData, menuNames))
					),
					map(({ documents }) =>
						Object.entries(documents).reduce(
							(menuItemDictionary, [menuItemType, menuItems]) => ({
								...menuItemDictionary,
								[kebabCase(menuItemType)]: menuItems
									.filter((item) => MenuService.filterByDeviceType(item, deviceType))
									.map(MenuService.normalizeItem),
							}),
							{}
						)
					),
					map(MenuService.normalizeGroups),
					take(1)
				)
			)
		).pipe(map((groups) => Object.assign({}, ...groups)));
	}
}
